import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import type { ElementKey, ElementsMap, GetVisibleElements, OverflowElement } from './types';

export const useDynamicOverflow = <T>(options: { getVisibleElements: GetVisibleElements<T> }) => {
  const { getVisibleElements } = options;

  const elementsMap = useRef<ElementsMap<T>>({});

  const [isMeasuring, setIsMeasuring] = useState(true);
  const [overflowElements, setOverflowElements] = useState<OverflowElement<T>[]>([]);

  useLayoutEffect(() => {
    if (!isMeasuring) return;

    const newOverflowElements: OverflowElement<T>[] = [];
    const allElements = Object.entries(elementsMap.current).map(
      ([elementKey, { boundaries, data }]) => ({ elementKey, boundaries, data }),
    );
    const visibleElements = getVisibleElements(allElements);

    allElements.forEach(({ elementKey }) => {
      const isVisible = visibleElements.includes(elementKey);

      elementsMap.current[elementKey].isHidden = !isVisible;

      if (!isVisible) {
        newOverflowElements.push({
          elementKey,
          data: elementsMap.current[elementKey].data,
        });
      }
    });

    setOverflowElements(newOverflowElements);
    setIsMeasuring(false);
  }, [getVisibleElements, isMeasuring]);

  const registerElement = useCallback(
    (elementKey: ElementKey, element: HTMLElement | null, data: T) => {
      if (!element) return;

      elementsMap.current[elementKey] = {
        data,
        isHidden: false,
        boundaries: element.getBoundingClientRect(),
      };
    },
    [],
  );

  const isElementHidden = useCallback(
    (elementKey: ElementKey) => !isMeasuring && elementsMap.current[elementKey]?.isHidden,
    [isMeasuring],
  );

  return useMemo(
    () => ({
      overflowElements: isMeasuring ? [] : overflowElements,
      registerElement,
      isElementHidden,
      refresh: () => setIsMeasuring(true),
    }),
    [isElementHidden, isMeasuring, overflowElements, registerElement],
  );
};
